<template>
  <div class="product-insight-container">
    <div v-if="loadingProduct" class="loading-state">
      <page-loader />
    </div>

    <div v-if="!loadingProduct" class="product-insight-content">
      <div class="product-insight-grid">
        <!-- Left: Product Preview -->
        <div class="product-insight-grid__left">
          <!-- Mobile collapse toggle -->
          <div class="mobile-preview-toggle" @click="mobilePreviewExpanded = !mobilePreviewExpanded">
            <div class="mobile-toggle-info">
              <img v-if="productMainImage" :src="productMainImage" class="mobile-toggle-thumb" alt="Product" />
              <span class="mobile-toggle-title">{{ product.title || 'Product' }}</span>
            </div>
            <v-icon size="18" color="#5f6368">
              {{ mobilePreviewExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
            </v-icon>
          </div>

          <!-- Preview content -->
          <div class="preview-content-wrap" :class="{ 'mobile-open': mobilePreviewExpanded }">
            <PreviewProduct :product="product" @go-back="goToProducts" />
          </div>
        </div>

        <!-- Right: Chat -->
        <div class="product-insight-grid__right">
          <div class="chatbot-container-wrapper">
            <div class="chatbot-wrapper">
              <AdsContent ref="adsContent" :product="product" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { productApi } from '@/apis/product';
import imageHelpers from '@/helpers/image';
import AdsContent from './components/AdsContent.vue';
import PreviewProduct from './components/PreviewProduct.vue';
import RobotIcon from '@/assets/images/robot.webp';
import AdsIcon from '@/assets/images/ads.webp';

export default {
  components: { AdsContent, PreviewProduct },
  name: 'ProductInsight',
  data() {
    return {
      product: {},
      loadingProduct: false,
      prevHtmlOverflow: null,
      prevBodyOverflow: null,
      mobilePreviewExpanded: false,
      leaveConfirmed: false,
      RobotIcon,
      AdsIcon,
    };
  },
  computed: {
    productMainImage() {
      if (!this.product.images || !this.product.images.length) return null;
      return imageHelpers.url(this.product.images[0].src, { width: 80, height: 80 });
    },
  },
  methods: {
    lockScroll() {
      if (typeof document === 'undefined') return;
      window.scrollTo(0, 0);
      this.prevHtmlOverflow = document.documentElement.style.overflow || '';
      this.prevBodyOverflow = document.body.style.overflow || '';
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    },
    unlockScroll() {
      if (typeof document === 'undefined') return;
      document.documentElement.style.overflow = this.prevHtmlOverflow || '';
      document.body.style.overflow = this.prevBodyOverflow || '';
      this.prevHtmlOverflow = null;
      this.prevBodyOverflow = null;
    },
    fitContainerHeight() {
      const rect = this.$el.getBoundingClientRect();
      this.$el.style.height = `calc(100vh - ${rect.top}px)`;
    },
    async getProduct() {
      this.loadingProduct = true;
      try {
        const res = await productApi.getById(this.$route.params.id);
        this.product = res.data.product;
      } catch (error) {
        console.log('error', error);
      } finally {
        this.loadingProduct = false;
      }
    },
    canLeavePage() {
      const adsContent = this.$refs.adsContent;
      if (!adsContent || typeof adsContent.shouldBlockNavigation !== 'function') return true;
      if (!adsContent.shouldBlockNavigation()) return true;
      return adsContent.confirmLeaveWhileProcessing();
    },
    goToProducts() {
      if (!this.canLeavePage()) return;
      this.leaveConfirmed = true;
      this.unlockScroll();
      this.$router.push('/admin/products');
    },
  },
  mounted() {
    this.lockScroll();
    this.$nextTick(() => {
      this.fitContainerHeight();
    });
    this.getProduct();
  },
  beforeRouteLeave(to, from, next) {
    if (this.leaveConfirmed) {
      this.leaveConfirmed = false;
      this.unlockScroll();
      next();
      return;
    }
    if (!this.canLeavePage()) {
      next(false);
      return;
    }
    this.unlockScroll();
    next();
  },
  beforeDestroy() {
    this.unlockScroll();
  },
};
</script>

<style lang="scss" scoped>
/* Container */
.product-insight-container {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.loading-state {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.product-insight-content {
  min-height: 0;
  overflow: hidden;
  flex: 1 1 0;
}

/* Grid */
.product-insight-grid {
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 0;
  height: 100%;
  min-height: 0;
  background: #ffffff;
  border-radius: 10px;
  overflow: hidden;

  @media (max-width: 1400px) {
    grid-template-columns: 290px 1fr;
  }

  @media (max-width: 1100px) {
    grid-template-columns: 260px 1fr;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr;
  }
}

/* Left panel */
.product-insight-grid__left {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  border-right: 1px solid var(--v-border-base);
}

/* Mobile collapse toggle */
.mobile-preview-toggle {
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 12px;
    cursor: pointer;
    gap: 12px;
    background: #f3f4f6;
    position: sticky;
    top: 0;
    z-index: 2;

    &:active {
      background: #eff0f1;
    }
  }
}

.mobile-toggle-info {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1;
}

.mobile-toggle-thumb {
  width: 36px;
  height: 36px;
  border-radius: 6px;
  object-fit: cover;
  border: 1px solid var(--v-border-base);
  flex-shrink: 0;
}

.mobile-toggle-title {
  font-size: 14px;
  font-weight: 600;
  color: #1a1a2e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Preview content wrap (mobile collapse) */
.preview-content-wrap {
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;

  @media (max-width: 768px) {
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.3s ease;

    &.mobile-open {
      flex: auto;
      max-height: 100vh;
      overflow-y: auto;
    }
  }
}

/* Right panel */
.product-insight-grid__right {
  min-height: 0;
  overflow: hidden;
  display: flex;
  background: #ffffff;
}

.chatbot-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.chatbot-container-wrapper {
  height: 100%;
  width: 100%;
  flex: 1 1 0;
  min-height: 0;
  border-radius: 0;
}
</style>
